import type { GeoJsonFeatureCollection } from '@gpx-spa/geo-domain'
import { getMapStyleFromFeatureProperties } from '@gpx-spa/geo-domain'
import type { Feature, FeatureCollection } from 'geojson'
import L, { type LeafletMouseEvent, type Map as LeafletMap } from 'leaflet'
import type { GlifyLayer } from 'leaflet.glify'
import type { MapInteractionController } from '../shared/map-interaction-controller.js'
import {
  getLeafletGlify,
  glifyHoverCursor,
  keepGlifyLineBrush,
  silenceGlifyPointer,
} from './leaflet-glify-api.js'
import { cssColorToGlify, leafletLineWidthToGlifyWeight } from './leaflet-glify-color.js'
import { GLIFY_PANE } from './leaflet-constants.js'
import type { LeafletHitRouter } from './leaflet-hit-router.js'
import { polygonStrokeLineFeatures } from './partition-glify-geojson.js'

export {
  partitionGlifyGeoJsonFeatures,
  polygonStrokeLineFeatures,
} from './partition-glify-geojson.js'

function ensureGlifyPane(map: LeafletMap): void {
  if (!map.getPane(GLIFY_PANE)) {
    map.createPane(GLIFY_PANE)
    const pane = map.getPane(GLIFY_PANE)
    if (pane) pane.style.zIndex = '440'
  }
}

type LayerFeatures = {
  lines: GeoJsonFeatureCollection['features']
  polygons: GeoJsonFeatureCollection['features']
}

/**
 * One shared WebGL lines canvas + one shapes canvas for the whole Leaflet map.
 * Per-layer glify.points/lines/shapes each open a browser WebGL context; Chrome
 * drops older contexts (~8–16) which leaves opaque white canvases on the map.
 */
export type LeafletGlifyGeoJsonPool = {
  setLayerFeatures: (
    layerId: string,
    features: LayerFeatures | null,
  ) => void
  clearLayer: (layerId: string) => void
  clearAll: () => void
}

export function createLeafletGlifyGeoJsonPool(options: {
  map: LeafletMap
  getInteractionController: () => MapInteractionController | null
  getHitRouter?: () => LeafletHitRouter | null
  /** WebGL init failed. The provider drops glify canvases and redraws on canvas. */
  onWebGlFailed?: (error: unknown) => void
}): LeafletGlifyGeoJsonPool {
  const { map, getInteractionController, getHitRouter, onWebGlFailed } = options
  ensureGlifyPane(map)
  const glifyCursor = glifyHoverCursor(() => getHitRouter?.() ?? null, 'glify')

  /** glify line hits are in degrees. The default 0.1° swallows polygon fills at city zoom. */
  function syncLineHitSlop(): void {
    const layer = linesGl as {
      settings?: { sensitivity?: number; sensitivityHover?: number }
    } | null
    if (!layer?.settings) return
    const center = map.getCenter()
    const origin = map.latLngToContainerPoint(center)
    const shifted = map.containerPointToLatLng(L.point(origin.x + 8, origin.y))
    const slop = Math.max(
      Math.abs(shifted.lng - center.lng),
      Math.abs(shifted.lat - center.lat),
    )
    layer.settings.sensitivity = slop
    layer.settings.sensitivityHover = slop
  }
  map.on('zoomend', syncLineHitSlop)

  const byLayer = new Map<string, LayerFeatures>()
  let linesGl: GlifyLayer | null = null
  let shapesGl: GlifyLayer | null = null
  /** Flat feature lists last pushed to glify (index → feature for click). */
  let flatLines: GeoJsonFeatureCollection['features'] = []
  let flatPolygons: GeoJsonFeatureCollection['features'] = []
  let rebuildQueued = false
  /** Bump when lines/shapes mount options change so setData cannot keep a stale brush/keys. */
  const LINES_STYLE_REV = 4
  const SHAPES_STYLE_REV = 4
  let mountedLinesRev = 0
  let mountedShapesRev = 0
  /** setData keeps the click/hover callbacks from the first mount. */
  let linesClicksBound = false
  let shapesClicksBound = false

  function destroyGl(): void {
    linesGl?.remove()
    shapesGl?.remove()
    linesGl = null
    shapesGl = null
    mountedLinesRev = 0
    mountedShapesRev = 0
    linesClicksBound = false
    shapesClicksBound = false
    flatLines = []
    flatPolygons = []
  }

  function layerIdFromFeature(feature: unknown): string | undefined {
    const props = (feature as Feature | null)?.properties as
      | Record<string, unknown>
      | null
      | undefined
    const id = props?.__gpx_glify_layer_id
    return typeof id === 'string' ? id : undefined
  }

  function stamp(layerId: string, features: GeoJsonFeatureCollection['features']) {
    return features.map((f) => ({
      ...f,
      properties: {
        ...(f.properties as Record<string, unknown> | null),
        __gpx_glify_layer_id: layerId,
      },
    }))
  }

  function featureClick(e: LeafletMouseEvent, feature: unknown): boolean | undefined {
    if (getHitRouter()?.occludesVector(e.containerPoint)) return undefined
    const interactionController = getInteractionController()
    if (!interactionController?.hasInteractions()) return undefined
    const layerId = layerIdFromFeature(feature)
    if (!layerId) return undefined
    void interactionController.dispatchHit({
      trigger: 'click',
      latlng: { lat: e.latlng.lat, lng: e.latlng.lng },
      nativeEvent: e,
      target: {
        type: 'feature',
        layerId,
        feature: feature as GeoJsonFeatureCollection['features'][number],
        position: { lat: e.latlng.lat, lng: e.latlng.lng },
      },
    })
    return true
  }

  function rebuild(): void {
    flatLines = []
    flatPolygons = []

    for (const [layerId, feats] of byLayer) {
      flatLines.push(...stamp(layerId, feats.lines))
      const polys = stamp(layerId, feats.polygons)
      flatPolygons.push(...polys)
      // Stroke via lines — shapes border:true kills fills (DEPTH_TEST without depth clear).
      flatLines.push(...stamp(layerId, polygonStrokeLineFeatures(feats.polygons)))
    }

    if (flatLines.length === 0) {
      linesGl?.remove()
      linesGl = null
    }
    if (flatPolygons.length === 0) {
      shapesGl?.remove()
      shapesGl = null
    }
    if (flatLines.length === 0 && flatPolygons.length === 0) return

    const glify = getLeafletGlify()
    const interactionController = getInteractionController()
    const wantClicks = Boolean(interactionController?.hasInteractions())

    if (flatLines.length > 0) {
      const collection = {
        type: 'FeatureCollection',
        features: flatLines,
      } as FeatureCollection
      if (linesGl && mountedLinesRev === LINES_STYLE_REV && linesClicksBound === wantClicks) {
        // Prefer setData — destroy+recreate races Leaflet redraw against a null map.
        linesGl.setData(collection)
      } else {
        linesGl?.remove()
        linesGl = glify.lines({
          map,
          data: collection,
          pane: GLIFY_PANE,
          className: 'leaflet-glify-lines',
          opacity: 1,
          weight: (i) => {
            const s = getMapStyleFromFeatureProperties(
              flatLines[i]?.properties as Record<string, unknown> | undefined,
            )
            return leafletLineWidthToGlifyWeight(s.lineWidth)
          },
          color: (i) => {
            const s = getMapStyleFromFeatureProperties(
              flatLines[i]?.properties as Record<string, unknown> | undefined,
            )
            return cssColorToGlify(s.lineColor ?? '#333333', s.lineOpacity ?? 1)
          },
          click: wantClicks ? featureClick : undefined,
          hover: wantClicks ? glifyCursor.hover : undefined,
          hoverOff: wantClicks ? glifyCursor.hoverOff : undefined,
        })
        silenceGlifyPointer(linesGl)
        keepGlifyLineBrush(linesGl)
        mountedLinesRev = LINES_STYLE_REV
        linesClicksBound = wantClicks
      }
      syncLineHitSlop()
    }

    if (flatPolygons.length > 0) {
      const collection = {
        type: 'FeatureCollection',
        features: flatPolygons,
      } as FeatureCollection
      // glify.shapes builds LatLng(coord[lngKey], coord[latKey]) — opposite of points/lines.
      // Singleton longitudeFirst() then projects GeoJSON fills off-map (border may still show).
      if (shapesGl && mountedShapesRev === SHAPES_STYLE_REV && shapesClicksBound === wantClicks) {
        shapesGl.setData(collection)
      } else {
        shapesGl?.remove()
        shapesGl = glify.shapes({
          map,
          data: collection,
          pane: GLIFY_PANE,
          className: 'leaflet-glify-shapes',
          // GeoJSON [lng,lat] + shapes' LatLng(coord[lngKey], coord[latKey]) quirk.
          latitudeKey: 0,
          longitudeKey: 1,
          // border:true enables DEPTH_TEST without clearing depth → fill discarded, outline only.
          border: false,
          opacity: 1,
          color: (i) => {
            const s = getMapStyleFromFeatureProperties(
              flatPolygons[i]?.properties as Record<string, unknown> | undefined,
            )
            return cssColorToGlify(s.fill ?? s.stroke ?? '#3388ff', s.fillOpacity ?? 0.35)
          },
          click: wantClicks ? featureClick : undefined,
          hover: wantClicks ? glifyCursor.hover : undefined,
          hoverOff: wantClicks ? glifyCursor.hoverOff : undefined,
        })
        silenceGlifyPointer(shapesGl)
        mountedShapesRev = SHAPES_STYLE_REV
        shapesClicksBound = wantClicks
      }
    }
  }

  function scheduleRebuild(): void {
    if (rebuildQueued) return
    rebuildQueued = true
    queueMicrotask(() => {
      rebuildQueued = false
      try {
        rebuild()
      } catch (error) {
        destroyGl()
        const pane = map.getPane(GLIFY_PANE)
        pane?.querySelectorAll('canvas').forEach((canvas) => canvas.remove())
        onWebGlFailed?.(error)
      }
    })
  }

  return {
    setLayerFeatures(layerId, features) {
      if (!features || (features.lines.length === 0 && features.polygons.length === 0)) {
        byLayer.delete(layerId)
      } else {
        byLayer.set(layerId, features)
      }
      scheduleRebuild()
    },
    clearLayer(layerId) {
      if (!byLayer.has(layerId)) return
      byLayer.delete(layerId)
      scheduleRebuild()
    },
    clearAll() {
      byLayer.clear()
      rebuildQueued = false
      destroyGl()
      map.off('zoomend', syncLineHitSlop)
    },
  }
}

/** @deprecated Prefer {@link createLeafletGlifyGeoJsonPool} — kept for call-site typing during migration. */
export type LeafletGlifyGeoJsonHandle = {
  clear: () => void
}

/**
 * @deprecated Opens a new WebGL context per call; use {@link createLeafletGlifyGeoJsonPool}.
 */
export function mountLeafletGlifyGeoJson(options: {
  map: LeafletMap
  layerId: string
  lines: GeoJsonFeatureCollection['features']
  polygons: GeoJsonFeatureCollection['features']
  interactionController: MapInteractionController | null
}): LeafletGlifyGeoJsonHandle {
  const pool = createLeafletGlifyGeoJsonPool({
    map: options.map,
    getInteractionController: () => options.interactionController,
  })
  pool.setLayerFeatures(options.layerId, {
    lines: options.lines,
    polygons: options.polygons,
  })
  return {
    clear: () => pool.clearAll(),
  }
}
